import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  Grid,
  CircularProgress
} from '@mui/material';
import { ArrowBack, OpenInNew } from '@mui/icons-material';
import dayjs from 'dayjs';

const FlightLogEntryDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/crew/flight-log/${id}`);
        setEntry(res.data);
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!entry) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography sx={{ mt: 2 }}>Flight Log Entry not found.</Typography>
      </Box>
    );
  }

  const createdBy = entry.createdBy
    ? `${entry.createdBy.firstName || ''} ${entry.createdBy.lastName || ''}`.trim()
    : 'N/A';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Chip label={entry.entryType || 'N/A'} size="small" />
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Flight Log Entry Details
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Entry ID: {entry._id} • Created: {entry.createdAt ? dayjs(entry.createdAt).format('DD MMM YYYY, HH:mm') : 'N/A'} • By: {createdBy}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Sector</Typography>
            <Typography variant="body2">{entry.sectorId?._id || entry.sectorId || 'N/A'}</Typography>
            {entry.sectorId?._id && (
              <Button size="small" onClick={() => navigate(`/ops/sectors/${entry.sectorId._id}`)}>
                Open Sector
              </Button>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Remarks</Typography>
            <Typography variant="body2">{entry.remarks || '—'}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Takeoff / Landing</Typography>
            <Typography variant="body2">
              {entry.takeoffTime ? dayjs(entry.takeoffTime).format('DD MMM YYYY, HH:mm') : 'N/A'} →{' '}
              {entry.landingTime ? dayjs(entry.landingTime).format('DD MMM YYYY, HH:mm') : 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Flight Time / Engine Hours</Typography>
            <Typography variant="body2">
              {entry.flightTime != null ? `${entry.flightTime} h` : 'N/A'} • {entry.engineHours != null ? `${entry.engineHours} h` : 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Fuel Consumed</Typography>
            <Typography variant="body2">{entry.fuelConsumed != null ? entry.fuelConsumed : 'N/A'}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Landings</Typography>
            <Typography variant="body2">{entry.numberOfLandings != null ? entry.numberOfLandings : 'N/A'}</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>
          Attachments
        </Typography>
        {Array.isArray(entry.attachments) && entry.attachments.length > 0 ? (
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {entry.attachments.map((a) => (
              <li key={a._id || a}>
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ py: 0.5 }}>
                  <Typography variant="body2">{a.fileUrl || a._id || String(a)}</Typography>
                  {a._id && (
                    <Button size="small" endIcon={<OpenInNew />} onClick={() => window.open(`/media/${a._id}`, '_blank', 'noopener,noreferrer')}>
                      Open
                    </Button>
                  )}
                </Box>
              </li>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No attachments.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default FlightLogEntryDetailPage;

